import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../../api/problem";
import { Frame, Note } from "../../../widgets/shared";
import type { Widget } from "../../../widgets/types";
import { imageBlobQuery, imageConfig, pictureUrl } from "../images";

/**
 * The `image` widget (dashboard-images IMG-09): the organisation's picture fitted to its box, `alt` as its text; a
 * deleted picture says so (the editor's ⋯ › Change picks another).
 */
export function ImageWidget({ widget, orgId }: { widget: Widget; orgId: string }) {
  const { t } = useTranslation();
  const cfg = imageConfig(widget.config);
  const blob = useQuery({ ...imageBlobQuery(orgId, cfg?.asset ?? ""), enabled: !!cfg && !!orgId });
  const url = blob.data ? pictureUrl(blob.data) : undefined;
  const gone = blob.error instanceof ApiError && blob.error.status === 404;
  const state = !cfg || gone ? "empty" : blob.isError ? "failed" : url ? "normal" : "pending";
  return (
    <Frame widget={widget} state={state} title={widget.title}>
      {!cfg || gone ? (
        <Note>{t("images.removed")}</Note>
      ) : blob.isError ? (
        <Note tone="warn">{t("images.failed")}</Note>
      ) : url ? (
        <img src={url} alt={cfg.alt} className="min-h-0 w-full flex-1 object-contain" />
      ) : (
        <div
          role="status"
          aria-label={t("images.loading")}
          className="min-h-0 flex-1 animate-pulse rounded bg-gray-100"
        />
      )}
    </Frame>
  );
}
